<script>
import { mapActions } from 'vuex';
import Card from '../../components/Card.vue';
import SwitchButton from '../../components/SwitchButton.vue';
import i18n from '@/plugins/i18n';

export default {
    components: { Card, SwitchButton },

    data() {
        return {
            primaries: [],
            selectedPrimaryIds: [],
            customerDropdownOpen: false,

            // tree fetched once across all primaries
            tree: [],

            // facilities of the supplier company (plus a virtual "company based" facility = 0)
            facilities: [],
            selectedFacilityID: 0,

            // year/quarter selector state (mirrors kpi-monitoring)
            isYearly: true,
            maxYear: new Date().getFullYear() - 1,

            hasKpiModule: false,

            // Per-metric/sub-metric input drafts.
            // Key: `m_<id>` or `s_<id>` -> per-(year,quarter) -> { value, metrics_input_table_id }
            cellDrafts: {},
            cellById: {},

            // Lookups built from server response: metricInputs[id][year][quarter]={metrics_input_table_id,value}
            existingMetricInputs: {},
            existingSubMetricInputs: {},

            loadingPrimaries: true,
            loadingTree: false,
        };
    },

    computed: {
        showCustomerFilter() {
            return this.primaries.length > 1;
        },

        showEmptyState() {
            return !this.loadingPrimaries && this.primaries.length === 0;
        },

        hasSingleFacility() {
            // kpi-monitoring's convention: a single real facility plus the virtual "company based" one.
            return this.facilities.length <= 2;
        },

        availableYears() {
            const year = new Date().getFullYear();
            return [year, year - 1, year - 2, year - 3, year - 4, year - 5];
        },

        selectedYears() {
            return [this.maxYear - 2, this.maxYear - 1, this.maxYear];
        },

        availableQuarters() {
            return [1, 2, 3, 4];
        },

        switchButtonLabels() {
            return [
                i18n.t('kpisMetrics.yearly_metrics'),
                i18n.t('kpisMetrics.quarterly_metrics'),
            ];
        },

        selectedPrimaryIdSet() {
            return new Set(this.selectedPrimaryIds.map(Number));
        },

        // Builds two parallel category > KPI > metric trees: `interactive` at the top, `blurred`
        // below. A metric is interactive iff it's in the server's `requested` group AND at least one
        // currently-selected primary requested it. Everything else (the server's `rest` group, or
        // filtered out by the customer multiselect) lands in the blurred group.
        // A KPI/category appears in either group as long as it contributes at least one metric there.
        displayTree() {
            const selected = this.selectedPrimaryIdSet;
            const interactiveCategories = [];
            const blurredCategories = [];

            for (const cat of this.tree) {
                const interactiveByKpi = new Map();
                const blurredByKpi = new Map();

                const pushMetric = (map, kpi, metric) => {
                    let bucket = map.get(kpi.sust_kpi_id);
                    if (!bucket) {
                        bucket = {
                            sust_kpi_id: kpi.sust_kpi_id,
                            sust_kpi_name: kpi.sust_kpi_name,
                            metrics: [],
                        };
                        map.set(kpi.sust_kpi_id, bucket);
                    }
                    bucket.metrics.push(metric);
                };

                for (const kpi of (cat.requested || [])) {
                    for (const metric of (kpi.sust_metric_kpis || [])) {
                        const requesters = metric.requested_by_company_ids || [];
                        const matched_selection = requesters.some(id => selected.has(Number(id)));
                        const m = { ...metric, interactive: matched_selection };
                        pushMetric(matched_selection ? interactiveByKpi : blurredByKpi, kpi, m);
                    }
                }

                for (const kpi of (cat.rest || [])) {
                    for (const metric of (kpi.sust_metric_kpis || [])) {
                        const m = { ...metric, interactive: false };
                        pushMetric(blurredByKpi, kpi, m);
                    }
                }

                if (interactiveByKpi.size > 0) {
                    interactiveCategories.push({
                        sust_category_id: cat.sust_category_id,
                        sust_category_name: cat.sust_category_name,
                        kpis: Array.from(interactiveByKpi.values()),
                    });
                }
                if (blurredByKpi.size > 0) {
                    blurredCategories.push({
                        sust_category_id: cat.sust_category_id,
                        sust_category_name: cat.sust_category_name,
                        kpis: Array.from(blurredByKpi.values()),
                    });
                }
            }

            return { interactive: interactiveCategories, blurred: blurredCategories };
        },

        hasInteractive() {
            return this.displayTree.interactive.length > 0;
        },

        // Earliest "from year" across the currently-selected customers. With one customer selected this
        // is simply that customer's requested year; with several it's the earliest among them. Null when
        // none of the selected customers carry a year (e.g. legacy rows).
        requestedFromYear() {
            const selected = this.selectedPrimaryIdSet;
            const years = this.primaries
                .filter(p => selected.has(Number(p.company_id)) && p.from_year != null)
                .map(p => Number(p.from_year));
            if (years.length === 0) return null;
            return Math.min(...years);
        },

        hasBlurred() {
            return this.displayTree.blurred.length > 0;
        },

        disclaimerParts() {
            const full = this.$t('requested_metrics.plan_disclaimer');
            const linkLabel = this.$t('requested_metrics.plan_disclaimer_link_label');
            const idx = full.indexOf('#L#');
            if (idx === -1) return { before: full, link: linkLabel, after: '' };
            return {
                before: full.slice(0, idx),
                link: linkLabel,
                after: full.slice(idx + 3),
            };
        },

        customerSummary() {
            if (this.selectedPrimaryIds.length === 0) {
                return this.$t('requested_metrics.no_customer_selected');
            }
            if (this.selectedPrimaryIds.length === this.primaries.length) {
                return this.$t('requested_metrics.all_customers');
            }
            return this.primaries
                .filter(p => this.selectedPrimaryIdSet.has(Number(p.company_id)))
                .map(p => p.company_name)
                .join(', ');
        },
    },

    async mounted() {
        document.addEventListener('click', this._handleOutsideClick);
        await this._detectKpiModule();
        await this._loadPrimaries();
        if (this.primaries.length > 0) {
            this.selectedPrimaryIds = this.primaries.map(p => p.company_id);
            const qp = Number(this.$route.query.primary_company_id);
            if (qp && this.primaries.some(p => Number(p.company_id) === qp)) {
                this.selectedPrimaryIds = [qp];
            }
            await this._loadFacilities();
            await this._loadTree();
        }
    },

    beforeDestroy() {
        document.removeEventListener('click', this._handleOutsideClick);
    },

    methods: {
        ...mapActions('KPIsMetrics', [
            'getRequestedMetricsPrimaryCompanies',
            'getRequestedMetricsTree',
            'createRequestedMetricsInput',
            'updateRequestedMetricsInput',
            'getFacilities',
        ]),
        ...mapActions('company', ['getAppModules', 'getCompanyLicence']),

        // ----- bootstrapping -----

        async _detectKpiModule() {
            try {
                const licenceRes = await this.getCompanyLicence({ company_id: this.$user.company_id });
                const licence = (licenceRes.data && licenceRes.data[0]) || licenceRes.data;
                const planId = licence && licence.plan_id;
                if (!planId) { this.hasKpiModule = false; return; }
                const modulesRes = await this.getAppModules({ plan_id: planId });
                const modules = modulesRes.data || [];
                this.hasKpiModule = modules.some(m => Number(m.module_id || m.app_module_id) === 4);
            } catch (e) {
                this.hasKpiModule = false;
            }
        },

        async _loadPrimaries() {
            this.loadingPrimaries = true;
            try {
                const res = await this.getRequestedMetricsPrimaryCompanies();
                this.primaries = res.data || [];
            } catch (e) {
                this.primaries = [];
            } finally {
                this.loadingPrimaries = false;
            }
        },

        async _loadFacilities() {
            try {
                const res = await this.getFacilities({ company_id: this.$user.company_id, is_deleted: false });
                const list = (res.data || []).slice();
                list.push({ facility_id: 0, facility_name: i18n.t('kpisMetrics.company_based') });
                this.facilities = list;
            } catch (e) {
                this.facilities = [{ facility_id: 0, facility_name: i18n.t('kpisMetrics.company_based') }];
            }

            // kpi-monitoring convention: single real facility (+ the virtual company-based) -> use the
            // real facility so inputs stick to it. Multi-facility -> default to company-based (0) so the
            // user can switch via the facility dropdown.
            if (this.facilities.length === 2) {
                this.selectedFacilityID = this.facilities[0].facility_id;
            } else {
                this.selectedFacilityID = 0;
            }
        },

        async _loadTree() {
            this.loadingTree = true;
            try {
                const res = await this.getRequestedMetricsTree({ facility_id: this.selectedFacilityID });
                this.tree = (res.data && res.data.categories) || [];
                this._buildExistingInputLookups();
                this.cellDrafts = {};
                this.cellById = {};

                // Diagnostic: confirm the response actually carries company_inputs and the lookup
                // tables are populated. Remove once verified.
                const inputCount = Object.keys(this.existingMetricInputs).reduce(
                    (acc, id) => acc + Object.keys(this.existingMetricInputs[id]).reduce(
                        (acc2, y) => acc2 + Object.keys(this.existingMetricInputs[id][y]).length, 0,
                    ), 0,
                );
                console.log('[requestedMetrics] tree loaded:', {
                    categories: this.tree.length,
                    metricInputsLoaded: inputCount,
                    sampleMetricWithInputs: this._sampleMetricWithInputs(),
                });
            } catch (e) {
                this.tree = [];
                this.existingMetricInputs = {};
                this.existingSubMetricInputs = {};
                this.cellDrafts = {};
                this.cellById = {};
            } finally {
                this.loadingTree = false;
            }
        },

        _sampleMetricWithInputs() {
            for (const cat of this.tree) {
                for (const group of [cat.requested, cat.rest]) {
                    for (const kpi of (group || [])) {
                        for (const metric of (kpi.sust_metric_kpis || [])) {
                            if (metric.company_inputs && metric.company_inputs.length > 0) {
                                return {
                                    metric_kpi_id: metric.sust_metric_kpi_id,
                                    company_inputs: metric.company_inputs,
                                };
                            }
                        }
                    }
                }
            }
            return null;
        },

        _buildExistingInputLookups() {
            const metricInputs = {};
            const subMetricInputs = {};
            for (const category of this.tree) {
                for (const group of [category.requested, category.rest]) {
                    for (const kpi of (group || [])) {
                        for (const metric of (kpi.sust_metric_kpis || [])) {
                            for (const input of (metric.company_inputs || [])) {
                                const id = metric.sust_metric_kpi_id;
                                if (!metricInputs[id]) metricInputs[id] = {};
                                if (!metricInputs[id][input.year]) metricInputs[id][input.year] = {};
                                metricInputs[id][input.year][input.quarter] = {
                                    metrics_input_table_id: input.metrics_input_table_id,
                                    value: input.value,
                                };
                            }
                            for (const sub of (metric.sust_sub_metric_kpis || [])) {
                                for (const input of (sub.company_inputs || [])) {
                                    const id = sub.sust_sub_metric_kpi_id;
                                    if (!subMetricInputs[id]) subMetricInputs[id] = {};
                                    if (!subMetricInputs[id][input.year]) subMetricInputs[id][input.year] = {};
                                    subMetricInputs[id][input.year][input.quarter] = {
                                        metrics_input_table_id: input.metrics_input_table_id,
                                        value: input.value,
                                    };
                                }
                            }
                        }
                    }
                }
            }
            this.existingMetricInputs = metricInputs;
            this.existingSubMetricInputs = subMetricInputs;
        },

        // ----- multiselect helpers -----

        toggleCustomerDropdown(e) {
            e.stopPropagation();
            this.customerDropdownOpen = !this.customerDropdownOpen;
        },

        _handleOutsideClick() {
            this.customerDropdownOpen = false;
        },

        isPrimarySelected(id) {
            return this.selectedPrimaryIdSet.has(Number(id));
        },

        togglePrimary(id) {
            const next = new Set(this.selectedPrimaryIds.map(Number));
            const numId = Number(id);
            if (next.has(numId)) next.delete(numId);
            else next.add(numId);
            this.selectedPrimaryIds = Array.from(next);
        },

        selectAllPrimaries() {
            this.selectedPrimaryIds = this.primaries.map(p => p.company_id);
        },

        clearAllPrimaries() {
            this.selectedPrimaryIds = [];
        },

        // ----- top bar controls -----

        async onFacilityChange() {
            // Refetch the tree so `company_inputs` are filtered to the newly-selected facility.
            await this._loadTree();
        },

        async setSwitch(state) {
            this.isYearly = state;
        },

        changeMaxYear(direction) {
            const yearsRange = this.availableYears.length;
            if (direction === 'left' && (this.maxYear - 2 !== this.availableYears[yearsRange - 1])) {
                this.maxYear -= 1;
            } else if (direction === 'right' && this.maxYear !== this.availableYears[0]) {
                this.maxYear += 1;
            }
        },

        // ----- cell value helpers -----

        cellKey(metric, sub_metric, year, quarter) {
            const prefix = sub_metric ? `s_${sub_metric.sust_sub_metric_kpi_id}` : `m_${metric.sust_metric_kpi_id}`;
            return `${prefix}|${year}|${quarter}`;
        },

        // Returns the value to display in the cell input. Pulls from local draft first, then the
        // server-side cache built from `company_inputs`. Coerces the cached value to a primitive
        // (PG returns NUMERIC as a string) so :value binding works on <input type="number">.
        cellValue(metric, sub_metric, year, quarter) {
            const k = this.cellKey(metric, sub_metric, year, quarter);
            if (Object.prototype.hasOwnProperty.call(this.cellDrafts, k)) {
                return this.cellDrafts[k];
            }
            const cached = this._readInputCache(metric, sub_metric, year, quarter);
            if (!cached) return '';
            if (cached.value === null || cached.value === undefined) return '';
            return cached.value;
        },

        setCell(metric, sub_metric, year, quarter, v) {
            const k = this.cellKey(metric, sub_metric, year, quarter);
            this.$set(this.cellDrafts, k, v);
        },

        async saveCell(metric, sub_metric, year, quarter) {
            const k = this.cellKey(metric, sub_metric, year, quarter);
            const raw = Object.prototype.hasOwnProperty.call(this.cellDrafts, k) ? this.cellDrafts[k] : null;
            if (raw === '' || raw === null || raw === undefined) return;

            const numericValue = Number(raw);
            if (Number.isNaN(numericValue)) return;

            const cached = this._readInputCache(metric, sub_metric, year, quarter);
            const existingId = cached ? cached.metrics_input_table_id : null;

            if (cached && Number(cached.value) === numericValue && existingId) return;

            const params = {
                company_id: this.$user.company_id,
                facility_id: this.selectedFacilityID,
                year,
                quarter,
                value: numericValue,
            };
            if (sub_metric) params.sub_metric_kpi_id = sub_metric.sust_sub_metric_kpi_id;
            else params.metric_kpi_id = metric.sust_metric_kpi_id;

            try {
                if (existingId) {
                    params.updated_by = this.$user.user_id;
                    await this.updateRequestedMetricsInput({ metrics_input_table_id: existingId, body: params });
                    this._upsertInputCache(metric, sub_metric, year, quarter, existingId, numericValue);
                } else {
                    params.created_by = this.$user.user_id;
                    const res = await this.createRequestedMetricsInput(params);
                    const newId = res && res.data && res.data.metrics_input_table_id;
                    if (newId) this._upsertInputCache(metric, sub_metric, year, quarter, newId, numericValue);
                }
            } catch (e) {
                this.$notify('error', this.extractMessageName(e, 'save_failed'));
            }
        },

        _readInputCache(metric, sub_metric, year, quarter) {
            const map = sub_metric ? this.existingSubMetricInputs : this.existingMetricInputs;
            const id = sub_metric ? sub_metric.sust_sub_metric_kpi_id : metric.sust_metric_kpi_id;
            if (!map[id] || !map[id][year] || !map[id][year][quarter]) return null;
            return map[id][year][quarter];
        },

        _upsertInputCache(metric, sub_metric, year, quarter, metrics_input_table_id, value) {
            const map = sub_metric ? this.existingSubMetricInputs : this.existingMetricInputs;
            const id = sub_metric ? sub_metric.sust_sub_metric_kpi_id : metric.sust_metric_kpi_id;
            if (!map[id]) this.$set(map, id, {});
            if (!map[id][year]) this.$set(map[id], year, {});
            this.$set(map[id][year], quarter, { metrics_input_table_id, value });
        },

        extractMessageName(err, fallback) {
            if (err && err.response && err.response.data && err.response.data.messageName) {
                return err.response.data.messageName;
            }
            return fallback;
        },

        // ----- navigation -----

        // Mirrors KpiMonitoring.goToKpi: opens /kpi-metrics in a new tab with the hierarchy prefilled.
        goToKpi(category_id, metric) {
            const url = this.$router.resolve({
                path: '/kpi-metrics',
                query: {
                    category: category_id,
                    element: metric.sust_element_id,
                    target: metric.sust_target_id,
                    kpi: metric.sust_kpi_id,
                    metric: metric.sust_metric_kpi_id,
                },
            }).href;
            window.open(url, '_blank');
        },

        // ----- visuals -----

        effectiveSubMetricCount(metric) {
            const subs = metric && metric.sust_sub_metric_kpis;
            if (!subs || subs.length < 2) return 0;
            return subs.length;
        },

        getColor(id) {
            if (id == 1) return '#fa8a14';
            if (id == 2) return '#44a900';
            if (id == 3) return '#de0222';
            if (id == 4) return '#004578';
            return 'var(--sustainfinity-navy)';
        },
    },
};
</script>

<template>
    <div class="requested-metrics-page">
        <div v-if="loadingPrimaries" class="loading-state">
            {{ $t('requested_metrics.loading') }}
        </div>

        <div v-else-if="showEmptyState" class="empty-state">
            {{ $t('requested_metrics.empty') }}
        </div>

        <template v-else>
            <!-- Top bar: facility select, yearly/quarterly switch, year stepper, customer filter -->
            <div class="top-select-inputs">
                <select v-if="!hasSingleFacility" class="monitor-page-facility-select"
                    v-model="selectedFacilityID" @change="onFacilityChange">
                    <option v-for="(facility, index) in facilities" :key="index" :value="facility.facility_id">
                        {{ facility.facility_name }}
                    </option>
                </select>

                <switch-button class="switch-button" @buttonState="setSwitch($event)"
                    :labelOn="switchButtonLabels[0]" :labelOff="switchButtonLabels[1]"
                    :colorOn="'var(--sustainfinity-navy)'" :colorOff="'var(--sustainfinity-blue)'"
                    :thumbColor="'whitesmoke'" :initialValue="isYearly" />

                <div class="year-selector" v-if="isYearly">
                    <i class="fa-solid fa-caret-left" @click="changeMaxYear('left')"></i>
                    {{ maxYear - 2 }} - {{ maxYear }}
                    <i class="fa-solid fa-caret-right" @click="changeMaxYear('right')"></i>
                </div>

                <div class="year-selector" v-else>
                    <i class="fa-solid fa-caret-left" @click="changeMaxYear('left')"></i>
                    {{ maxYear }}
                    <i class="fa-solid fa-caret-right" @click="changeMaxYear('right')"></i>
                </div>

                <div v-if="showCustomerFilter" class="customer-multiselect" @click.stop>
                    <span class="filter-label">{{ $t('requested_metrics.primary_company_label') }}</span>
                    <div class="picker" @click="toggleCustomerDropdown($event)">
                        <span class="picker-value" :title="customerSummary">{{ customerSummary }}</span>
                        <i class="fa-solid fa-caret-down"></i>
                        <div v-if="customerDropdownOpen" class="picker-dropdown" @click.stop>
                            <div class="picker-actions">
                                <button type="button" @click="selectAllPrimaries">
                                    {{ $t('requested_metrics.select_all') }}
                                </button>
                                <button type="button" @click="clearAllPrimaries">
                                    {{ $t('requested_metrics.clear_all') }}
                                </button>
                            </div>
                            <div v-for="p in primaries" :key="p.company_id" class="picker-option">
                                <input type="checkbox"
                                    :id="`primary_${p.company_id}`"
                                    :checked="isPrimarySelected(p.company_id)"
                                    @change="togglePrimary(p.company_id)" />
                                <label :for="`primary_${p.company_id}`">{{ p.company_name }}</label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="loadingTree" class="loading-state">
                {{ $t('requested_metrics.loading') }}
            </div>

            <template v-else>
                <Card width="100" v-if="!hasInteractive && !hasBlurred">
                    <div class="empty-tree">{{ $t('requested_metrics.no_metrics_for_primary') }}</div>
                </Card>

                <!-- Top section: requested + interactive (passes the customer multiselect). -->
                <template v-if="hasInteractive">
                    <div class="section-heading">
                        {{ $t('requested_metrics.section_requested') }}
                        <span v-if="requestedFromYear" class="section-heading-year">
                            {{ $t('requested_metrics.from_year_suffix', { year: requestedFromYear }) }}
                        </span>
                    </div>
                    <div v-for="category in displayTree.interactive" :key="`int_${category.sust_category_id}`"
                        class="category-section">
                        <div class="category-name">
                            <div class="category-name--border"
                                :style="{ borderBottomColor: getColor(category.sust_category_id), color: getColor(category.sust_category_id) }">
                                {{ category.sust_category_name }}
                            </div>
                        </div>

                        <div v-for="kpi in category.kpis" :key="`int_kpi_${kpi.sust_kpi_id}`" class="category-card">
                            <div class="element-name" :style="{ color: getColor(category.sust_category_id) }">
                                <span>{{ kpi.sust_kpi_name }}</span>
                            </div>

                            <div class="headers">
                                <div :style="{ backgroundColor: getColor(category.sust_category_id) }" class="header-flex">
                                    {{ $t('kpisMetrics.metrics').toUpperCase() }}
                                </div>
                                <div v-for="period in isYearly ? selectedYears : availableQuarters" :key="period"
                                    :style="{ backgroundColor: getColor(category.sust_category_id) }" class="value-flex">
                                    <div class="value-cell-value">
                                        {{ isYearly ? period : `Q${period}` }}
                                    </div>
                                </div>
                            </div>

                            <template v-for="metric in kpi.metrics">
                                <div v-if="effectiveSubMetricCount(metric) === 0"
                                    :key="`int_m_${metric.sust_metric_kpi_id}`"
                                    class="values">
                                    <div class="header-flex">
                                        <a @click="goToKpi(category.sust_category_id, metric)">
                                            {{ metric.sust_metric_kpi_name }}
                                        </a>
                                    </div>
                                    <div v-for="period in isYearly ? selectedYears : availableQuarters" :key="period" class="value-flex">
                                        <input type="number"
                                            :value="cellValue(metric, null, isYearly ? period : maxYear, isYearly ? 5 : period)"
                                            @input="setCell(metric, null, isYearly ? period : maxYear, isYearly ? 5 : period, $event.target.value)"
                                            @blur="saveCell(metric, null, isYearly ? period : maxYear, isYearly ? 5 : period)" />
                                    </div>
                                </div>
                                <template v-else>
                                    <div :key="`int_m_head_${metric.sust_metric_kpi_id}`" class="values">
                                        <div class="header-flex">
                                            <a @click="goToKpi(category.sust_category_id, metric)">
                                                {{ metric.sust_metric_kpi_name }}
                                            </a>
                                        </div>
                                        <div v-for="period in isYearly ? selectedYears : availableQuarters" :key="period" class="value-flex">
                                            <span class="muted">-</span>
                                        </div>
                                    </div>
                                    <div v-for="sub in metric.sust_sub_metric_kpis"
                                        :key="`int_s_${sub.sust_sub_metric_kpi_id}`"
                                        class="sub-values">
                                        <div class="header-flex sub-metric-cell">
                                            {{ sub.sust_sub_metric_kpi_name }}
                                        </div>
                                        <div v-for="period in isYearly ? selectedYears : availableQuarters" :key="period" class="value-flex">
                                            <input type="number"
                                                :value="cellValue(metric, sub, isYearly ? period : maxYear, isYearly ? 5 : period)"
                                                @input="setCell(metric, sub, isYearly ? period : maxYear, isYearly ? 5 : period, $event.target.value)"
                                                @blur="saveCell(metric, sub, isYearly ? period : maxYear, isYearly ? 5 : period)" />
                                        </div>
                                    </div>
                                </template>
                            </template>
                        </div>
                    </div>
                </template>

                <!-- Disclaimer only when supplier lacks the KPI module and there are blurred rows. -->
                <div v-if="!hasKpiModule && hasBlurred" class="plan-disclaimer">
                    <span>{{ disclaimerParts.before }}</span><a
                        href="https://www.sustainfinity.com/contact-us"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="plan-disclaimer-link"
                    >{{ disclaimerParts.link }}</a><span>{{ disclaimerParts.after }}</span>
                </div>

                <!-- Bottom section: blurred (non-requested OR filtered out by the customer multiselect). -->
                <template v-if="hasBlurred">
                    <div class="section-heading">{{ $t('requested_metrics.section_rest') }}</div>
                    <div v-for="category in displayTree.blurred" :key="`blur_${category.sust_category_id}`"
                        class="category-section">
                        <div class="category-name">
                            <div class="category-name--border"
                                :style="{ borderBottomColor: getColor(category.sust_category_id), color: getColor(category.sust_category_id) }">
                                {{ category.sust_category_name }}
                            </div>
                        </div>

                        <div v-for="kpi in category.kpis" :key="`blur_kpi_${kpi.sust_kpi_id}`" class="category-card">
                            <div class="element-name" :style="{ color: getColor(category.sust_category_id) }">
                                <span>{{ kpi.sust_kpi_name }}</span>
                            </div>

                            <div class="headers">
                                <div :style="{ backgroundColor: getColor(category.sust_category_id) }" class="header-flex">
                                    {{ $t('kpisMetrics.metrics').toUpperCase() }}
                                </div>
                                <div v-for="period in isYearly ? selectedYears : availableQuarters" :key="period"
                                    :style="{ backgroundColor: getColor(category.sust_category_id) }" class="value-flex">
                                    <div class="value-cell-value">
                                        {{ isYearly ? period : `Q${period}` }}
                                    </div>
                                </div>
                            </div>

                            <template v-for="metric in kpi.metrics">
                                <div v-if="effectiveSubMetricCount(metric) === 0"
                                    :key="`blur_m_${metric.sust_metric_kpi_id}`"
                                    class="values" :class="{ blurred: !hasKpiModule }">
                                    <div class="header-flex">
                                        <a v-if="hasKpiModule" @click="goToKpi(category.sust_category_id, metric)">{{ metric.sust_metric_kpi_name }}</a>
                                        <a v-else>{{ metric.sust_metric_kpi_name }}</a>
                                    </div>
                                    <div v-for="period in isYearly ? selectedYears : availableQuarters" :key="period" class="value-flex">
                                        <input type="number" :disabled="!hasKpiModule"
                                            :value="cellValue(metric, null, isYearly ? period : maxYear, isYearly ? 5 : period)"
                                            @input="setCell(metric, null, isYearly ? period : maxYear, isYearly ? 5 : period, $event.target.value)"
                                            @blur="saveCell(metric, null, isYearly ? period : maxYear, isYearly ? 5 : period)" />
                                    </div>
                                </div>
                                <template v-else>
                                    <div :key="`blur_m_head_${metric.sust_metric_kpi_id}`" class="values" :class="{ blurred: !hasKpiModule }">
                                        <div class="header-flex">
                                            <a v-if="hasKpiModule" @click="goToKpi(category.sust_category_id, metric)">{{ metric.sust_metric_kpi_name }}</a>
                                            <a v-else>{{ metric.sust_metric_kpi_name }}</a>
                                        </div>
                                        <div v-for="period in isYearly ? selectedYears : availableQuarters" :key="period" class="value-flex">
                                            <span class="muted">-</span>
                                        </div>
                                    </div>
                                    <div v-for="sub in metric.sust_sub_metric_kpis"
                                        :key="`blur_s_${sub.sust_sub_metric_kpi_id}`"
                                        class="sub-values" :class="{ blurred: !hasKpiModule }">
                                        <div class="header-flex sub-metric-cell">
                                            {{ sub.sust_sub_metric_kpi_name }}
                                        </div>
                                        <div v-for="period in isYearly ? selectedYears : availableQuarters" :key="period" class="value-flex">
                                            <input type="number" :disabled="!hasKpiModule"
                                                :value="cellValue(metric, sub, isYearly ? period : maxYear, isYearly ? 5 : period)"
                                                @input="setCell(metric, sub, isYearly ? period : maxYear, isYearly ? 5 : period, $event.target.value)"
                                                @blur="saveCell(metric, sub, isYearly ? period : maxYear, isYearly ? 5 : period)" />
                                        </div>
                                    </div>
                                </template>
                            </template>
                        </div>
                    </div>
                </template>
            </template>
        </template>
    </div>
</template>

<style scoped lang="scss">
.requested-metrics-page {
    width: 100%;
    padding: 2em;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1em;

    > * { width: 100%; }
}

.loading-state,
.empty-state {
    width: 100%;
    text-align: center;
    padding: 4em 1em;
    font-size: 1.2em;
    color: var(--sustainfinity-navy);
}

.empty-state {
    font-weight: bold;
}

.top-select-inputs {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-content: flex-start;
    align-items: flex-start;
    width: 100%;
    padding-bottom: .5em;
    margin-bottom: .5em;

    > .monitor-page-facility-select {
        border: 0;
        border-bottom: thin solid var(--sustainfinity-navy);
        outline: 0;
        background: 0;
        padding: .33em .66em .33em .66em;
        cursor: pointer;

        &:focus {
            outline: thin solid var(--sustainfinity-navy);
        }
    }

    > .year-selector {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-content: center;
        align-items: center;
        margin-left: 3em;
        color: gray;

        > i {
            transform: translateY(5%);
            cursor: pointer;
            transition: all 100ms;
            color: gray;

            &:hover {
                color: darkgray;
            }
        }

        > .fa-caret-left {
            padding-right: .5em;
        }

        > .fa-caret-right {
            padding-left: .5em;
        }
    }
}

.switch-button {
    margin-left: 3em;
}

.customer-multiselect {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: .5em;
    position: relative;
    margin-left: auto;

    > .filter-label {
        font-weight: bold;
        color: var(--sustainfinity-navy);
    }

    > .picker {
        min-width: 18em;
        max-width: 32em;
        background-color: white;
        border: thin solid #ccc;
        border-radius: .4em;
        padding: .35em .6em;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: .5em;
        position: relative;
        font-size: .85em;

        > .picker-value {
            flex: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
}

.picker-dropdown {
    position: absolute;
    top: calc(100% + .25em);
    left: 0;
    right: 0;
    z-index: 50;
    background-color: white;
    border: thin solid #ccc;
    border-radius: .4em;
    max-height: 16em;
    overflow-y: auto;
    box-shadow: 0 .25em .5em rgba(0, 0, 0, 0.12);

    > .picker-actions {
        display: flex;
        gap: .5em;
        padding: .5em .75em;
        border-bottom: thin solid #eee;

        > button {
            font-size: .75em;
            padding: .15em .5em;
            border: thin solid #ddd;
            border-radius: .25em;
            background-color: white;
            cursor: pointer;
        }
    }

    > .picker-option {
        display: flex;
        align-items: center;
        gap: .5em;
        padding: .35em .75em;
        font-size: .85em;

        > label {
            margin-bottom: 0;
            cursor: pointer;
        }

        &:hover {
            background-color: #f5f5f5;
        }
    }
}

.plan-disclaimer {
    width: 100%;
    background-color: var(--sustainfinity-blue);
    color: whitesmoke;
    padding: .75em 1em;
    border-radius: .5em;
    font-size: .9em;
    line-height: 1.4;
    box-shadow: 0 .1em .3em rgba(0, 0, 0, 0.1);

    > .plan-disclaimer-link {
        color: whitesmoke;
        text-decoration: underline;
        font-weight: 600;

        &:hover, &:focus {
            color: #fff;
            text-decoration: none;
        }
    }
}

.section-heading {
    width: 100%;
    font-size: 1.15rem;
    font-weight: bold;
    color: var(--sustainfinity-navy);
    text-transform: uppercase;
    letter-spacing: .05em;
    border-bottom: thin solid #d4dddf;
    padding-bottom: .25em;
    margin-top: 1em;

    > .section-heading-year {
        font-size: .85rem;
        font-weight: 600;
        text-transform: none;
        letter-spacing: normal;
        color: var(--sustainfinity-blue);
        margin-left: .5em;
    }
}

/* ---- kpi-monitoring table parity ---- */

.category-section {
    width: 100%;
    margin-bottom: 2em;
}

.category-name {
    font-size: 1.66rem;
    font-weight: bold;
    color: #023047;
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.category-name--border {
    border-bottom: medium solid;
    max-width: fit-content;
    margin-right: 2.5%;
}

.category-card {
    position: relative;
    filter: drop-shadow(0 .25em .33em gray);
    border: thin solid lightgray;
    border-radius: 1em;
    margin-top: 1em;
    margin-right: 2em;
    background-color: #f5f5f5;
    padding: 1em;
}

.element-name {
    font-weight: bold;
    font-size: 1rem;
    padding-bottom: 1em;
    padding-left: .5em;
}

.header-flex {
    flex: 3;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    > a {
        cursor: pointer;
        color: inherit;
        text-decoration: none;

        &:hover {
            text-decoration: underline !important;
        }
    }
}

.value-flex {
    flex: 1;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    > .value-cell-value {
        flex: 9;
        text-align: center;
    }

    > input {
        width: 90%;
        padding: .2em .35em;
        border-radius: .25em;
        border: thin solid #ccc;
        background-color: white;
        font-size: .9em;
        text-align: center;

        &:focus {
            outline: thin solid var(--sustainfinity-navy);
        }

        &:disabled {
            background-color: transparent;
            color: #777;
            cursor: not-allowed;
        }
    }
}

.headers {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    margin-left: .33em;
    font-size: .9rem;
}

.headers > div {
    border-top-left-radius: .33em;
    border-top-right-radius: .33em;
    margin-right: .33em;
    padding: .1em;
    color: white;
    font-weight: bolder;
    filter: drop-shadow(0 0 .33em lightgray);
}

.values {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: stretch;
    width: 100%;
    margin-left: .33em;
    font-size: .8rem;
}

.values > div {
    margin-right: .33em;
    padding: .1em;
    color: #023047;
    font-weight: bold;
    background-color: #f3f3f3;
    filter: drop-shadow(0 0 .33em lightgray);
}

.sub-values {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: stretch;
    width: 100%;
    margin-left: .33em;
    font-size: .8rem;
    text-align: center;
}

.sub-values > div {
    margin-right: .33em;
    padding: .1em;
    color: #023047;
    font-weight: bold;
    background-color: #f3f3f3;
    border-bottom: thin dashed lightgray;
    filter: drop-shadow(0 0 .33em lightgray);
}

/* Blur rule for non-interactive metric rows (filtered-out or non-requested). */
.values.blurred,
.sub-values.blurred {
    filter: blur(2px);
    pointer-events: none;
    user-select: none;
    opacity: .85;
}

.muted { color: #aaa; }

.empty-tree {
    text-align: center;
    padding: 2em;
    color: #666;
    font-style: italic;
}
</style>
